import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Dropdown, Label, ListBox, Pagination } from '@heroui/react';
import { DataGrid, InlineSelect, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    BRAND_COLUMN,
    BRAND_ROW_ACTIONS,
    BRANDS_PAGE_SIZE_OPTIONS,
    NO_IMAGE_ICON,
    getPaginationWindow,
    type Brand,
    type BrandRowAction,
} from '../brandsMeta';

type BrandTableProps = {
    data: Brand[];
    onDeleteBrand: (id: string) => void;
    onEditBrand: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

/**
 * Render-only brands grid. The data is **server-paginated** — `data` is already
 * the current page in the server's order (by name), so there's no client-side
 * sort or slice here; the footer just drives the page / page-size controls.
 * Columns aren't sortable because the API has no sort parameter.
 */
export const BrandTable: React.FC<BrandTableProps> = ({
    data,
    onDeleteBrand,
    onEditBrand,
    onPageChange,
    onPageSizeChange,
    page,
    pageSize,
    totalCount,
    totalPages,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            tableAria: t('brands.components.brandTable.tableAria'),
            actionsAria: t('brands.components.brandTable.actionsAria'),
            previous: t('brands.components.brandTable.pagination.previous'),
            next: t('brands.components.brandTable.pagination.next'),
            perPage: t('brands.components.brandTable.pagination.perPage'),
            columns: {
                image: t('brands.components.brandTable.columns.image'),
                name: t('brands.components.brandTable.columns.name'),
            },
            empty: {
                title: t('brands.components.brandTable.empty.title'),
            },
        }),
        [t],
    );

    const onRowAction = React.useCallback(
        (id: string, action: BrandRowAction) => {
            if (action === 'edit') {
                onEditBrand(id);
                return;
            }
            onDeleteBrand(id);
        },
        [onDeleteBrand, onEditBrand],
    );

    const columns = React.useMemo<DataGridColumn<Brand>[]>(
        () => [
            {
                id: BRAND_COLUMN.image,
                header: copy.columns.image,
                width: 72,
                cell: (item) =>
                    item.imageUrl ? (
                        <img
                            alt={item.name}
                            className="size-10 rounded-[8px] object-cover"
                            src={item.imageUrl}
                            {...getNodeTestIdProp(`brand-row-image-${item.id}`)}
                        />
                    ) : (
                        <div
                            aria-hidden
                            className="flex size-10 items-center justify-center rounded-[8px] bg-surface-secondary text-muted"
                            {...getNodeTestIdProp(
                                `brand-row-image-fallback-${item.id}`,
                            )}
                        >
                            <Icon className="size-5" icon={NO_IMAGE_ICON} />
                        </div>
                    ),
            },
            {
                id: BRAND_COLUMN.name,
                header: copy.columns.name,
                accessorKey: 'name',
                isRowHeader: true,
                cell: (item) => (
                    <span
                        className="text-sm font-medium text-foreground"
                        {...getNodeTestIdProp(`brand-row-name-${item.id}`)}
                    >
                        {item.name}
                    </span>
                ),
            },
            {
                id: BRAND_COLUMN.actions,
                header: '',
                pinned: 'end',
                width: 40,
                align: 'center',
                cellClassName: 'bg-surface',
                headerClassName: 'bg-surface-secondary',
                cell: (item) => (
                    <div
                        className="flex justify-center"
                        {...getNodeTestIdProp(`brand-row-actions-${item.id}`)}
                    >
                        <Dropdown>
                            <Button
                                aria-label={copy.actionsAria}
                                isIconOnly
                                size="sm"
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    `brand-row-actions-trigger-${item.id}`,
                                )}
                            >
                                <Icon icon="gravity-ui:ellipsis-vertical" />
                            </Button>
                            <Dropdown.Popover className="min-w-[160px]">
                                <Dropdown.Menu
                                    onAction={(key) =>
                                        onRowAction(
                                            item.id,
                                            key as BrandRowAction,
                                        )
                                    }
                                >
                                    {BRAND_ROW_ACTIONS.map((action) => (
                                        <Dropdown.Item
                                            id={action.id}
                                            key={action.id}
                                            textValue={t(action.labelKey)}
                                            variant={action.variant}
                                            {...getNodeTestIdProp(
                                                `brand-row-action-${action.id}-${item.id}`,
                                            )}
                                        >
                                            <Icon icon={action.icon} />
                                            <Label>{t(action.labelKey)}</Label>
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown.Popover>
                        </Dropdown>
                    </div>
                ),
            },
        ],
        [copy, onRowAction, t],
    );

    const pageWindow = getPaginationWindow(totalPages, page);

    return (
        <Widget
            // Full width, like every other table in the app. BOS-149 capped
            // this one at `max-w-2xl` because two columns — a 72px thumbnail
            // and a name — stretched across a desktop read as a table that had
            // failed to load its other columns (BOS-170). In place it was
            // worse: the one table narrower than its neighbours looked broken
            // rather than deliberate. The sparseness is a missing Products
            // count, not a width problem, and that needs a field `GET
            // /brands/list` does not return yet.
            className="flex flex-col gap-0 overflow-hidden p-0"
            {...getNodeTestIdProp('brand-table-widget')}
        >
            <DataGrid<Brand>
                aria-label={copy.tableAria}
                columns={columns}
                data={data}
                getRowId={(item) => item.id}
                renderEmptyState={() => (
                    <div
                        className="flex flex-col items-center gap-3 py-12 text-muted"
                        {...getNodeTestIdProp('brand-table-empty')}
                    >
                        <Icon
                            aria-hidden
                            className="size-8 text-default-500"
                            icon="gravity-ui:folder"
                        />
                        <span
                            className="text-sm"
                            {...getNodeTestIdProp('brand-table-empty-title')}
                        >
                            {copy.empty.title}
                        </span>
                    </div>
                )}
                variant="primary"
                {...getNodeTestIdProp('brand-table-grid')}
            />
            {totalCount > BRANDS_PAGE_SIZE_OPTIONS[0]! ? (
                <div
                    className="flex flex-wrap items-center justify-between gap-3 border-t border-separator p-3"
                    {...getNodeTestIdProp('brand-table-pagination')}
                >
                    <div className="flex items-center gap-2 text-xs text-muted">
                        <span>{copy.perPage}</span>
                        <InlineSelect
                            aria-label={copy.perPage}
                            onChange={(value) =>
                                value && onPageSizeChange(Number(value))
                            }
                            value={String(pageSize)}
                            {...getNodeTestIdProp('brand-table-per-page')}
                        >
                            <InlineSelect.Trigger>
                                <InlineSelect.Value />
                                <InlineSelect.Indicator />
                            </InlineSelect.Trigger>
                            <InlineSelect.Popover className="w-[96px]">
                                <ListBox>
                                    {BRANDS_PAGE_SIZE_OPTIONS.map((size) => (
                                        <ListBox.Item
                                            id={String(size)}
                                            key={size}
                                            textValue={String(size)}
                                        >
                                            {size}
                                            <ListBox.ItemIndicator />
                                        </ListBox.Item>
                                    ))}
                                </ListBox>
                            </InlineSelect.Popover>
                        </InlineSelect>
                    </div>
                    {totalPages > 1 ? (
                        <Pagination size="sm">
                            <Pagination.Content>
                                <Pagination.Item>
                                    <Pagination.Previous
                                        aria-label={copy.previous}
                                        isDisabled={page === 1}
                                        onPress={() => onPageChange(page - 1)}
                                    >
                                        <Pagination.PreviousIcon />
                                    </Pagination.Previous>
                                </Pagination.Item>
                                {pageWindow.map((entry, index) =>
                                    entry === 'ellipsis' ? (
                                        <Pagination.Item
                                            key={`brand-table-page-ellipsis-${index}`}
                                        >
                                            <Pagination.Ellipsis />
                                        </Pagination.Item>
                                    ) : (
                                        <Pagination.Item key={entry}>
                                            <Pagination.Link
                                                isActive={entry === page}
                                                onPress={() =>
                                                    onPageChange(entry)
                                                }
                                                {...getNodeTestIdProp(
                                                    `brand-table-page-${entry}`,
                                                )}
                                            >
                                                {entry}
                                            </Pagination.Link>
                                        </Pagination.Item>
                                    ),
                                )}
                                <Pagination.Item>
                                    <Pagination.Next
                                        aria-label={copy.next}
                                        isDisabled={page === totalPages}
                                        onPress={() => onPageChange(page + 1)}
                                    >
                                        <Pagination.NextIcon />
                                    </Pagination.Next>
                                </Pagination.Item>
                            </Pagination.Content>
                        </Pagination>
                    ) : null}
                </div>
            ) : null}
        </Widget>
    );
};
